<script setup>
// Base de conhecimento (RAG) da conta: adiciona texto ou sobe arquivo
// (pdf/txt/docx), lista os itens e o status do processamento (embeddings).
// O agente consulta isso pela ferramenta "Buscar KB" (tool_search_kb).
import { ref, reactive, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import knowledgeAPI from 'dashboard/api/agents/knowledge';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  agentId: { type: Number, default: null },
});

const { t } = useI18n();
const items = ref([]);
const loading = ref(false);
const saving = ref(false);
// Métricas do cérebro de aprendizado (faixa no topo, visível em ambos os filtros).
const stats = ref(null);
const showGaps = ref(false);
const title = ref('');
const content = ref('');
const file = ref(null);
const fileInput = ref(null);
// Escopo do item: 'agent' = só este agente (padrão), 'global' = todos os agentes.
const scope = ref('agent');
// Filtro de revisão: 'active' (padrão) ou 'needs_review' (itens migrados do legado).
const reviewFilter = ref('active');

const STATUS = {
  pending: { label: 'pending', color: 'text-n-amber-11' },
  processing: { label: 'processing', color: 'text-n-amber-11' },
  ready: { label: 'ready', color: 'text-n-teal-11' },
  error: { label: 'error', color: 'text-n-ruby-11' },
};

const fetchItems = async () => {
  loading.value = true;
  try {
    const { data } = await knowledgeAPI.getForAgent(
      props.agentId,
      reviewFilter.value
    );
    items.value = data;
  } finally {
    loading.value = false;
  }
};

// Carrega a faixa de métricas do cérebro (aprendizados, auto-confirmados, pendências, lacunas).
const fetchStats = async () => {
  const { data } = await knowledgeAPI.stats();
  stats.value = data;
};

const scopeLabel = item => {
  if (item.global) return t('AI_AGENTS.KNOWLEDGE.SCOPE.GLOBAL');
  if (props.agentId && (item.agent_ids || []).includes(props.agentId)) {
    return t('AI_AGENTS.KNOWLEDGE.SCOPE.THIS');
  }
  return t('AI_AGENTS.KNOWLEDGE.SCOPE.OTHER');
};

// Alterna o escopo: global <-> só este agente.
const toggleScope = async item => {
  const next = item.global ? [props.agentId].filter(Boolean) : [];
  await knowledgeAPI.updateScope(item.id, next);
  fetchItems();
};

const onFile = e => {
  file.value = e.target.files?.[0] || null;
};

const reset = () => {
  title.value = '';
  content.value = '';
  file.value = null;
  if (fileInput.value) fileInput.value.value = '';
};

const add = async () => {
  if (!title.value || (!content.value && !file.value)) {
    useAlert(t('AI_AGENTS.KNOWLEDGE.NEED_CONTENT'));
    return;
  }
  saving.value = true;
  try {
    await knowledgeAPI.createItem({
      title: title.value,
      content: content.value,
      file: file.value,
      agentIds: scope.value === 'agent' && props.agentId ? [props.agentId] : [],
    });
    reset();
    useAlert(t('AI_AGENTS.KNOWLEDGE.ADDED'));
    fetchItems();
  } catch (e) {
    useAlert(e?.message || t('AI_AGENTS.KNOWLEDGE.ERROR'));
  } finally {
    saving.value = false;
  }
};

const remove = async item => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!window.confirm(t('AI_AGENTS.KNOWLEDGE.CONFIRM_DELETE'))) return;
  await knowledgeAPI.delete(item.id);
  fetchItems();
};

// Confirma um item migrado do acervo legado (needs_review -> active).
const confirmItem = async item => {
  await knowledgeAPI.confirm(item.id);
  useAlert(t('AI_AGENTS.KNOWLEDGE.CONFIRMED'));
  fetchItems();
  fetchStats();
};

// Desfaz uma confirmação (inclusive automática): item volta para "A revisar".
const unconfirmItem = async item => {
  await knowledgeAPI.unconfirm(item.id);
  fetchItems();
  fetchStats();
};

// Edição inline de um candidato aprendido antes de confirmar.
const editingId = ref(null);
// Card expandido (clique no texto mostra o Q&A completo)
const expandedId = ref(null);
const toggleExpand = item => {
  expandedId.value = expandedId.value === item.id ? null : item.id;
};
const editForm = reactive({ title: '', content: '' });

const startEdit = item => {
  editingId.value = item.id;
  editForm.title = item.title;
  editForm.content = item.content || '';
};

const cancelEdit = () => {
  editingId.value = null;
};

const saveEdit = async () => {
  await knowledgeAPI.updateContent(editingId.value, { ...editForm });
  editingId.value = null;
  await fetchItems();
};

watch(reviewFilter, fetchItems);
onMounted(() => {
  fetchItems();
  fetchStats();
});
</script>

<template>
  <div class="space-y-6">
    <div>
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('AI_AGENTS.KNOWLEDGE.TITLE') }}
      </h3>
      <p class="mt-0.5 text-xs text-n-slate-11">
        {{ t('AI_AGENTS.KNOWLEDGE.SUBTITLE') }}
      </p>
    </div>

    <!-- Adicionar -->
    <div class="rounded-2xl border border-n-weak bg-n-solid-2 p-4 space-y-3">
      <Input
        v-model="title"
        :label="t('AI_AGENTS.KNOWLEDGE.NAME')"
        :placeholder="t('AI_AGENTS.KNOWLEDGE.NAME_PH')"
      />
      <TextArea
        v-model="content"
        :label="t('AI_AGENTS.KNOWLEDGE.TEXT')"
        :placeholder="t('AI_AGENTS.KNOWLEDGE.TEXT_PH')"
        :rows="4"
      />
      <!-- Escopo: só este agente (padrão) ou todos -->
      <div class="flex items-center gap-2 text-xs">
        <span class="text-n-slate-11">{{
          t('AI_AGENTS.KNOWLEDGE.SCOPE.LABEL')
        }}</span>
        <button
          type="button"
          class="rounded-lg border px-2.5 py-1"
          :class="
            scope === 'agent'
              ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
              : 'border-n-weak text-n-slate-11'
          "
          @click="scope = 'agent'"
        >
          {{ t('AI_AGENTS.KNOWLEDGE.SCOPE.THIS') }}
        </button>
        <button
          type="button"
          class="rounded-lg border px-2.5 py-1"
          :class="
            scope === 'global'
              ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
              : 'border-n-weak text-n-slate-11'
          "
          @click="scope = 'global'"
        >
          {{ t('AI_AGENTS.KNOWLEDGE.SCOPE.GLOBAL') }}
        </button>
      </div>
      <div class="flex items-center justify-between gap-3">
        <label
          class="flex cursor-pointer items-center gap-2 text-sm text-n-slate-11"
        >
          <span class="i-lucide-paperclip" />
          <span>{{ file ? file.name : t('AI_AGENTS.KNOWLEDGE.UPLOAD') }}</span>
          <input
            ref="fileInput"
            type="file"
            accept=".pdf,.txt,.docx"
            class="hidden"
            @change="onFile"
          />
        </label>
        <Button
          :label="t('AI_AGENTS.KNOWLEDGE.ADD')"
          :is-loading="saving"
          size="sm"
          @click="add"
        />
      </div>
      <p class="text-xs text-n-slate-10">
        {{ t('AI_AGENTS.KNOWLEDGE.HINT') }}
      </p>
    </div>

    <!-- Métricas do cérebro de aprendizado (visível em ambos os filtros) -->
    <div v-if="stats" class="flex flex-wrap gap-3">
      <div class="rounded-lg border border-n-weak px-3 py-2">
        <p class="text-[10px] uppercase text-n-slate-10">
          {{ t('AI_AGENTS.KNOWLEDGE.STATS.LEARNED_MONTH') }}
        </p>
        <p class="text-sm font-semibold text-n-slate-12">
          {{ stats.learned_month }}
        </p>
      </div>
      <div class="rounded-lg border border-n-weak px-3 py-2">
        <p class="text-[10px] uppercase text-n-slate-10">
          {{ t('AI_AGENTS.KNOWLEDGE.STATS.AUTO_CONFIRMED') }}
        </p>
        <p class="text-sm font-semibold text-n-slate-12">
          {{ stats.auto_confirmed_month }}
        </p>
      </div>
      <div class="rounded-lg border border-n-weak px-3 py-2">
        <p class="text-[10px] uppercase text-n-slate-10">
          {{ t('AI_AGENTS.KNOWLEDGE.STATS.PENDING') }}
        </p>
        <p class="text-sm font-semibold text-n-slate-12">
          {{ stats.pending }}
        </p>
      </div>
      <div class="relative rounded-lg border border-n-weak px-3 py-2">
        <button
          type="button"
          class="flex items-center gap-1.5 text-left"
          @click="showGaps = !showGaps"
        >
          <span>
            <span class="block text-[10px] uppercase text-n-slate-10">
              {{ t('AI_AGENTS.KNOWLEDGE.STATS.TOP_GAPS') }}
            </span>
            <span class="block text-sm font-semibold text-n-slate-12">
              {{ stats.top_gaps.length }}
            </span>
          </span>
          <span class="i-lucide-chevron-down text-n-slate-10" />
        </button>
        <div
          v-if="showGaps && stats.top_gaps.length"
          class="absolute left-0 top-full z-10 mt-1 w-64 rounded-lg border border-n-weak bg-n-solid-2 p-2 shadow-lg"
        >
          <p
            v-for="gap in stats.top_gaps"
            :key="gap.query"
            class="flex items-center justify-between gap-2 py-1 text-xs text-n-slate-11"
          >
            <span class="truncate">{{ gap.query }}</span>
            <span class="shrink-0 text-n-slate-10">{{ gap.count }}</span>
          </p>
        </div>
      </div>
    </div>

    <!-- Filtro: Ativos / A revisar -->
    <div class="flex items-center gap-2 text-xs">
      <button
        type="button"
        class="rounded-lg border px-2.5 py-1"
        :class="
          reviewFilter === 'active'
            ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
            : 'border-n-weak text-n-slate-11'
        "
        @click="reviewFilter = 'active'"
      >
        {{ t('AI_AGENTS.KNOWLEDGE.FILTER.ACTIVE') }}
      </button>
      <button
        type="button"
        class="rounded-lg border px-2.5 py-1"
        :class="
          reviewFilter === 'needs_review'
            ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
            : 'border-n-weak text-n-slate-11'
        "
        @click="reviewFilter = 'needs_review'"
      >
        {{ t('AI_AGENTS.KNOWLEDGE.FILTER.NEEDS_REVIEW') }}
      </button>
    </div>

    <!-- Lista -->
    <div v-if="loading" class="flex justify-center py-8">
      <Spinner size="lg" />
    </div>
    <div v-else-if="items.length" class="space-y-2">
      <div
        v-for="item in items"
        :key="item.id"
        class="flex items-start justify-between rounded-xl border border-n-weak bg-n-solid-2 p-3"
      >
        <div class="min-w-0 flex-1">
          <template v-if="editingId === item.id">
            <Input v-model="editForm.title" class="mb-2" />
            <TextArea v-model="editForm.content" :rows="3" />
          </template>
          <template v-else>
            <p
              class="flex flex-wrap items-center text-sm font-medium text-n-slate-12"
            >
              <span class="min-w-0 truncate">{{ item.title }}</span>
              <span
                v-if="item.review_state === 'needs_review'"
                class="ml-1 rounded-full border border-n-amber-6 bg-n-amber-3 px-1.5 py-0.5 text-[10px] text-n-amber-11"
              >
                {{ t('AI_AGENTS.KNOWLEDGE.NEEDS_REVIEW_BADGE') }}
              </span>
              <span
                v-if="item.learned_kind"
                class="ml-1 rounded-full border px-1.5 py-0.5 text-[10px]"
                :class="{
                  'border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11':
                    item.learned_kind === 'correction',
                  'border-n-amber-6 bg-n-amber-3 text-n-amber-11':
                    item.learned_kind === 'conflict',
                  'border-n-teal-6 bg-n-teal-3 text-n-teal-11':
                    item.learned_kind === 'mined' ||
                    item.learned_kind === 'gap',
                }"
              >
                {{
                  t(
                    `AI_AGENTS.KNOWLEDGE.LEARNED_KIND.${item.learned_kind.toUpperCase()}`
                  )
                }}
              </span>
              <span
                v-else-if="item.imported_from === 'learned'"
                class="ml-1 rounded-full border border-n-teal-6 bg-n-teal-3 px-1.5 py-0.5 text-[10px] text-n-teal-11"
              >
                {{ t('AI_AGENTS.KNOWLEDGE.LEARNED_BADGE') }}
              </span>
              <span
                v-if="item.auto_confirmed_at"
                class="ml-1 rounded-full border border-n-slate-6 bg-n-slate-3 px-1.5 py-0.5 text-[10px] text-n-slate-11"
              >
                {{ t('AI_AGENTS.KNOWLEDGE.AUTO_CONFIRMED') }}
              </span>
              <span
                v-if="item.occurrences > 1"
                class="ml-1 text-[10px] text-n-slate-10"
              >
                {{
                  t('AI_AGENTS.KNOWLEDGE.OCCURRENCES', {
                    count: item.occurrences,
                  })
                }}
              </span>
            </p>
            <p class="text-xs text-n-slate-10">
              {{ item.source_type }} ·
              <span :class="STATUS[item.status]?.color">
                {{ t(`AI_AGENTS.KNOWLEDGE.STATUS.${item.status}`) }}
              </span>
              <template v-if="item.status === 'ready'">
                · {{ item.chunks_count }} {{ t('AI_AGENTS.KNOWLEDGE.CHUNKS') }}
              </template>
            </p>
            <!-- Clique expande/recolhe o Q&A completo (a prévia corta em 2 linhas) -->
            <p
              v-if="item.review_state === 'needs_review' && item.content"
              class="mt-1 cursor-pointer whitespace-pre-wrap text-xs text-n-slate-11"
              :class="expandedId === item.id ? '' : 'line-clamp-2'"
              :title="t('AI_AGENTS.KNOWLEDGE.EXPAND_HINT')"
              @click="toggleExpand(item)"
            >
              {{ item.content }}
            </p>
            <template v-if="item.learned_kind === 'conflict'">
              <p class="mt-1 text-xs text-n-amber-11">
                {{
                  t('AI_AGENTS.KNOWLEDGE.CONFLICT_WITH', {
                    title: item.conflict_with_title,
                  })
                }}
              </p>
              <p class="text-xs text-n-slate-10">
                {{ t('AI_AGENTS.KNOWLEDGE.CONFLICT_CONFIRM_HINT') }}
              </p>
            </template>
          </template>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <template v-if="editingId === item.id">
            <button
              type="button"
              class="rounded-full border border-n-weak px-2 py-0.5 text-[11px] text-n-slate-11"
              @click="cancelEdit"
            >
              {{ t('AI_AGENTS.KNOWLEDGE.CANCEL') }}
            </button>
            <button
              type="button"
              class="flex items-center gap-1 rounded-full border border-n-teal-6 bg-n-teal-2 px-2 py-0.5 text-[11px] text-n-teal-11"
              @click="saveEdit"
            >
              {{ t('AI_AGENTS.KNOWLEDGE.SAVE_ITEM') }}
            </button>
          </template>
          <template v-else>
            <button
              v-if="item.review_state === 'needs_review'"
              type="button"
              class="rounded-full border border-n-weak px-2 py-0.5 text-[11px] text-n-slate-11"
              :title="t('AI_AGENTS.KNOWLEDGE.EDIT_ITEM')"
              @click="startEdit(item)"
            >
              {{ t('AI_AGENTS.KNOWLEDGE.EDIT_ITEM') }}
            </button>
            <button
              v-if="item.review_state === 'needs_review'"
              type="button"
              class="flex items-center gap-1 rounded-full border border-n-teal-6 bg-n-teal-2 px-2 py-0.5 text-[11px] text-n-teal-11"
              :title="t('AI_AGENTS.KNOWLEDGE.CONFIRM')"
              @click="confirmItem(item)"
            >
              <span class="i-lucide-check" />
              {{ t('AI_AGENTS.KNOWLEDGE.CONFIRM') }}
            </button>
            <button
              v-if="reviewFilter === 'active' && item.auto_confirmed_at"
              type="button"
              class="rounded-full border border-n-slate-6 bg-n-slate-2 px-2 py-0.5 text-[11px] text-n-slate-11"
              :title="t('AI_AGENTS.KNOWLEDGE.UNDO_CONFIRM')"
              @click="unconfirmItem(item)"
            >
              {{ t('AI_AGENTS.KNOWLEDGE.UNDO_CONFIRM') }}
            </button>
            <button
              type="button"
              class="rounded-full border px-2 py-0.5 text-[11px]"
              :class="
                item.global
                  ? 'border-n-amber-6 bg-n-amber-2 text-n-amber-11'
                  : 'border-n-teal-6 bg-n-teal-2 text-n-teal-11'
              "
              :title="t('AI_AGENTS.KNOWLEDGE.SCOPE.TOGGLE')"
              @click="toggleScope(item)"
            >
              {{ scopeLabel(item) }}
            </button>
            <button
              class="i-lucide-trash-2 text-n-ruby-9"
              @click="remove(item)"
            />
          </template>
        </div>
      </div>
    </div>
    <p v-else class="py-6 text-center text-sm text-n-slate-10">
      {{ t('AI_AGENTS.KNOWLEDGE.EMPTY') }}
    </p>
  </div>
</template>
